import { Controller } from '@hotwired/stimulus'

// Tailwind modal for seller trust badges.
// Overlay is moved to document.body so parent overflow / stacking never breaks it.
// After portaling, Stimulus targets disconnect - so we cache DOM refs on connect.
export default class extends Controller {
  static targets = ['overlay', 'panel', 'closeButton']

  connect () {
    this.previouslyFocused = null
    this.handleKeydown = this.onKeydown.bind(this)
    this.cacheElements()
    this.portalOverlay()
    this.bindDelegatedClicks()
  }

  disconnect () {
    this.unbindDelegatedClicks()
    this.close()
    this.restoreOverlay()
    this.overlayEl = null
    this.panelEl = null
    this.closeButtonEl = null
  }

  cacheElements () {
    this.overlayEl = this.hasOverlayTarget ? this.overlayTarget : null
    this.panelEl = this.hasPanelTarget ? this.panelTarget : null
    this.closeButtonEl = this.hasCloseButtonTarget ? this.closeButtonTarget : null
  }

  portalOverlay () {
    if (!this.overlayEl) return
    if (this.overlayEl.parentElement === document.body) return

    this.overlayHome = this.overlayEl.parentElement
    this.overlayNextSibling = this.overlayEl.nextElementSibling
    document.body.appendChild(this.overlayEl)
  }

  restoreOverlay () {
    if (!this.overlayEl || !this.overlayHome) return
    if (!document.body.contains(this.overlayEl)) {
      this.overlayHome = null
      this.overlayNextSibling = null
      return
    }

    if (this.overlayNextSibling && this.overlayNextSibling.parentElement === this.overlayHome) {
      this.overlayHome.insertBefore(this.overlayEl, this.overlayNextSibling)
    } else {
      this.overlayHome.appendChild(this.overlayEl)
    }
    this.overlayHome = null
    this.overlayNextSibling = null
  }

  // Clicks on the portaled overlay are outside the controller element,
  // so Stimulus data-action will not reach us - bind manually.
  bindDelegatedClicks () {
    if (!this.overlayEl || this.onOverlayClick) return

    this.onOverlayClick = (event) => {
      const actionEl = event.target.closest('[data-trust-modal-action]')
      if (actionEl && this.overlayEl.contains(actionEl)) {
        const action = actionEl.getAttribute('data-trust-modal-action')
        if (action === 'close') this.close(event)
        return
      }

      if (event.target === this.overlayEl) this.close(event)
    }

    this.overlayEl.addEventListener('click', this.onOverlayClick)
  }

  unbindDelegatedClicks () {
    if (!this.overlayEl || !this.onOverlayClick) return
    this.overlayEl.removeEventListener('click', this.onOverlayClick)
    this.onOverlayClick = null
  }

  open (event) {
    if (event) event.preventDefault()
    if (!this.overlayEl) return

    this.previouslyFocused = event?.currentTarget || document.activeElement
    this.overlayEl.classList.remove('hidden')
    this.overlayEl.setAttribute('aria-hidden', 'false')
    document.body.classList.add('overflow-hidden')
    document.addEventListener('keydown', this.handleKeydown)

    requestAnimationFrame(() => {
      if (this.closeButtonEl) this.closeButtonEl.focus()
    })
  }

  close (event) {
    if (event) event.preventDefault()
    if (!this.overlayEl) return

    this.overlayEl.classList.add('hidden')
    this.overlayEl.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('overflow-hidden')
    document.removeEventListener('keydown', this.handleKeydown)

    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus()
    }
    this.previouslyFocused = null
  }

  onKeydown (event) {
    if (event.key === 'Escape') this.close(event)
  }
}
